import React, { useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';

export interface AlertProps {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  title?: string;
  onClose?: () => void;
  autoHide?: boolean;
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  type,
  message,
  title,
  onClose,
  autoHide = false,
  className = '',
}) => {
  useEffect(() => {
    if (autoHide && message && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message, autoHide, onClose]);

  if (!message) return null;

  const typeConfig = {
    success: {
      bgColor: 'bg-success-50',
      borderColor: 'border-success-200',
      textColor: 'text-success-700',
      iconColor: 'text-success-500',
      iconFn: FiIcons.FiCheckCircle,
    },
    error: {
      bgColor: 'bg-error-50',
      borderColor: 'border-error-200',
      textColor: 'text-error-700',
      iconColor: 'text-error-500',
      iconFn: FiIcons.FiAlertCircle,
    },
    warning: {
      bgColor: 'bg-warning-50',
      borderColor: 'border-warning-200',
      textColor: 'text-warning-700',
      iconColor: 'text-warning-500',
      iconFn: FiIcons.FiAlertTriangle,
    },
    info: {
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200',
      textColor: 'text-primary-700',
      iconColor: 'text-primary-500',
      iconFn: FiIcons.FiInfo,
    },
  };

  const config = typeConfig[type];

  return (
    <div className={`${config.bgColor} ${config.borderColor} border rounded-xl p-4 shadow-xs ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {config.iconFn({ className: `h-5 w-5 ${config.iconColor}` })}
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${config.textColor} mb-1`}>
              {title}
            </h3>
          )}
          <div className={`text-sm ${config.textColor}`}>
            <p>{message}</p>
          </div>
        </div>
        {onClose && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={onClose}
              className={`inline-flex rounded-md ${config.textColor} hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-${type}-50 focus:ring-${type}-600`}
            >
              <span className="sr-only">Dismiss</span>
              {FiIcons.FiX({ className: "h-5 w-5" })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alert;
